<template>
  <div
    class="lg-input"
    :class="[rootClass, { 'has-lead': $slots.lead, 'has-trail': $slots.trail }]"
    :style="rootStyle"
  >
    <span v-if="$slots.lead" class="lg-input-lead"><slot name="lead" /></span>
    <input
      ref="el"
      class="lg-input-el"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :aria-label="ariaLabel"
      v-bind="inputAttrs"
      @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
    <span v-if="$slots.trail" class="lg-input-trail"><slot name="trail" /></span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';

defineOptions({ inheritAttrs: false });

withDefaults(
  defineProps<{
    modelValue?: string;
    type?: string;
    placeholder?: string;
    disabled?: boolean;
    autocomplete?: string;
    ariaLabel?: string;
  }>(),
  { modelValue: '', type: 'text' },
);

defineEmits<{ 'update:modelValue': [value: string] }>();

// `inheritAttrs: false` + manual `v-bind` on the inner <input> means Vue's
// default attr fallthrough (which would land `class`/`style` on the input)
// never happens — so we route it ourselves: `class`/`style` go on the root
// wrapper (that's what a consumer like `<LgInput class="search">` expects to
// size/position), everything else (placeholder overrides, aria-*, data-*,
// etc.) still reaches the actual <input>.
const attrs = useAttrs();
const rootClass = computed(() => attrs.class);
const rootStyle = computed(() => attrs.style);
const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs;
  return rest;
});

const el = ref<HTMLInputElement | null>(null);
defineExpose({ focus: () => el.value?.focus() });
</script>

<style scoped>
.lg-input {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  padding: 0 var(--lg-space-2);
  min-height: var(--lg-control-md);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border-strong);
  background: var(--lg-bg);
  color: var(--lg-text);
  transition: border-color var(--lg-dur-fast) var(--lg-ease);
}
.lg-input:focus-within {
  border-color: var(--lg-accent);
  box-shadow: var(--lg-focus);
}
.lg-input-lead,
.lg-input-trail {
  display: inline-flex;
  align-items: center;
  color: var(--lg-text-dim);
  flex-shrink: 0;
}
.lg-input-el {
  flex: 1;
  min-width: 0;
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-md);
  padding: var(--lg-space-2) 0;
  border: none;
  background: transparent;
  color: var(--lg-text);
}
.lg-input-el:focus {
  outline: none;
}
.lg-input-el::placeholder {
  color: var(--lg-text-faint);
}
.lg-input-el:disabled {
  cursor: default;
}
</style>
